import type { SignupOpts } from "@/hooks/userHook";
import type { GoogleProfile, User } from "@/models/user";
import { API_BASE, type APIResponse, callApi } from "@/services/api";
import { generateHMAC } from "@/services/hmac";

// Register a new user and get back user information and auth tokens
export async function signup(opts: SignupOpts): Promise<APIResponse<User>> {
  try {
    // Convert segment attributes to sa_* query parameters
    const queryParams = new URLSearchParams();

    if (opts.segmentAttributes) {
      Object.entries(opts.segmentAttributes).forEach(([key, value]) => {
        queryParams.append(`sa_${key}`, value);
      });
    }

    // Add UTM parameters to query string if they exist
    if (opts.utmCampaign) queryParams.append("utm_campaign", opts.utmCampaign);
    if (opts.utmSource) queryParams.append("utm_source", opts.utmSource);
    if (opts.utmMedium) queryParams.append("utm_medium", opts.utmMedium);
    if (opts.utmEntryUrl) queryParams.append("utm_entry_url", opts.utmEntryUrl);

    // Add invite code if it exists
    if (opts.inviteCode) queryParams.append("invite_code", opts.inviteCode);

    const queryString = queryParams.toString();
    const url = `${API_BASE}/auth${queryString ? `?${queryString}` : ""}`;

    // Prepare body without query parameters and segment attributes
    const {
      segmentAttributes: _segmentAttributes,
      utmCampaign: _utmCampaign,
      utmSource: _utmSource,
      utmMedium: _utmMedium,
      utmEntryUrl: _utmEntryUrl,
      inviteCode: _inviteCode,
      ...bodyOpts
    } = opts;

    const resp = await callApi<User>(url, {
      method: "POST",
      body: bodyOpts,
    });
    return resp;
  } catch (e) {
    throw e;
  }
}

/*
When a session is created, the response headers contain the a JWT token that should be sent
with the next request, in the same format it was recieved.
This token is on the "Authorization" header.
*/
export async function signin(opts: {
  email: string;
  password: string;
  rememberMe: boolean;
}): Promise<APIResponse<User>> {
  try {
    const resp = await callApi<User>(`${API_BASE}/auth/sign_in`, {
      method: "POST",
      body: opts,
    });
    return resp;
  } catch (e) {
    throw e;
  }
}

export async function signout(): Promise<APIResponse<User>> {
  try {
    const resp = await callApi<User>(`${API_BASE}/auth/sign_out`);
    return resp;
  } catch (e) {
    throw e;
  }
}

export async function googleSignin(
  opts: GoogleProfile,
): Promise<APIResponse<User>> {
  try {
    const resp = await callApi<User>(`${API_BASE}/auth/google`, {
      method: "POST",
      body: opts,
    });
    return resp;
  } catch (e) {
    throw e;
  }
}

/* By passing an email address to this endpoint, the password reset flow for that user is
triggered
*/

export async function requestResetPassword(
  email: string,
): Promise<APIResponse> {
  const opts = {
    email,
    redirectUrl: "/users/password/new",
  };
  try {
    return await callApi(`${API_BASE}/auth/password`, {
      method: "POST",
      body: opts,
    });
  } catch (e) {
    throw e;
  }
}

// Updates the password

export async function resetPassword(opts: {
  password: string;
  passwordConfirmation: string;
  resetPasswordToken: string;
}): Promise<APIResponse> {
  const hmac = await generateHMAC();
  try {
    return await callApi(`${API_BASE}/users/reset_password`, {
      method: "POST",
      body: opts,
      headers: hmac,
    });
  } catch (e) {
    throw e;
  }
}

// Redeems the token from an account-unlock email
export async function unlockAccount(opts: {
  unlockToken: string;
}): Promise<APIResponse<unknown>> {
  const hmac = await generateHMAC();
  try {
    return await callApi(`${API_BASE}/users/unlock_account`, {
      method: "POST",
      body: opts,
      headers: hmac,
    });
  } catch (e) {
    throw e;
  }
}

// Redeems the token from an email-confirmation (or change-of-email) email
export async function confirmEmail(opts: {
  confirmationToken: string;
}): Promise<APIResponse<unknown>> {
  const hmac = await generateHMAC();
  try {
    return await callApi(`${API_BASE}/users/confirm_email`, {
      method: "POST",
      body: opts,
      headers: hmac,
    });
  } catch (e) {
    throw e;
  }
}

export async function verifyEmail({
  email,
}: {
  email: string;
}): Promise<APIResponse & { result?: string }> {
  const hmac = await generateHMAC();
  try {
    return await callApi(
      `${API_BASE}/users/verify_email?email=${encodeURIComponent(email)}`,
      {
        headers: hmac,
      },
    );
  } catch (e) {
    throw e;
  }
}

export async function checkEmailExists({
  email,
}: {
  email: string;
}): Promise<APIResponse & { exists?: boolean }> {
  const hmac = await generateHMAC();
  try {
    return await callApi(
      `${API_BASE}/users/email_exists?email=${encodeURIComponent(email)}`,
      {
        headers: hmac,
      },
    );
  } catch (e) {
    throw e;
  }
}

export async function sendMagicSigninLink({
  email,
}: {
  email: string;
}): Promise<APIResponse> {
  const hmac = await generateHMAC();
  try {
    return await callApi(`${API_BASE}/auth/magic_link`, {
      body: { email },
      method: "POST",
      headers: hmac,
    });
  } catch (e) {
    throw e;
  }
}

export async function validateMagicSignin(opts: {
  token: string;
}): Promise<APIResponse<User>> {
  try {
    const resp = await callApi<User>(`${API_BASE}/auth/magic_link/validate`, {
      method: "POST",
      body: opts,
    });
    return resp;
  } catch (e) {
    throw e;
  }
}
